import type { FastlyImageApiPayload, PointOfInterest } from '~/types/elements';
import type { PublicationContext } from '~/types';

interface UseImageOptions {
  ratio?: string | string[];
  imageCrops?: Record<string, number[]>;
  poiData?: PointOfInterest;
  fp?: number[];
  isMatchAoi?: boolean;
  quality?: number;
  originalWidth?: number;
  originalHeight?: number;
}

interface RatioPercentage {
  original: number;
  block: number;
}

// Constants
const DEFAULT_RATIO = '3x2';
const DEFAULT_FOCAL_POINT = [0.5, 0];
const MIDDLE_FOCAL_POINT = [0.5, 0.5];
const CENTER_OFFSET = 0.5;
const APTOMA_HOST_REGEX = /^https:\/\/smooth-storage(-dev)?\.aptoma\.no\//;
const APTOMA_DEV_HOST_REGEX = /^https:\/\/smooth-storage-dev\.aptoma\.no\//;
const INTERNAL_HOST_REGEX = /^https:\/\/images(?:-test|-stage)?(?:\.dn\.no|-global\.nhst\.tech)/;

// Utility functions (pure functions moved outside for reusability and testing)
const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const roundToFactor = (width: number, factor?: number): number => {
  const rounded = Math.round(width);
  if (!factor || factor <= 0) { return rounded; }
  const remainder = rounded % factor;
  return remainder === 0 ? rounded : rounded + (factor - remainder);
};

const formatCropParam = (crop: number[], fallbackWidth: number, fallbackHeight: number): string =>
  crop.length === 4
    ? `${crop[2]},${crop[3]},x${crop[0]},y${crop[1]},safe`
    : `${fallbackWidth}:${fallbackHeight},smart`;

export default function useImage (src = '', opt: UseImageOptions = {}) {
  const rtConfig = useRuntimeConfig().public as unknown as PublicationContext;

  // Pre-computed values (computed once per composable instance)
  const aptomaHost = APTOMA_HOST_REGEX.test(src);
  const aptomaDevHost = APTOMA_DEV_HOST_REGEX.test(src);
  const externalHost = !INTERNAL_HOST_REGEX.test(src);

  const getRatio = (): string => {
    const ratios = opt.ratio === undefined
      ? [DEFAULT_RATIO]
      : (typeof opt.ratio === 'string' ? [opt.ratio] : opt.ratio);

    if (opt.imageCrops) {
      const matchedRatio = ratios.find(r => opt.imageCrops?.[r]);
      if (matchedRatio) { return matchedRatio; }
    }

    return ratios[0];
  };

  const ratio = getRatio();

  const getPoiDimension = (): number[] => {
    const { poiData } = opt;
    if (!poiData?.originalWidth || !poiData?.originalHeight ||
        poiData.x === undefined || poiData.y === undefined ||
        poiData.width === undefined || poiData.height === undefined) {
      return [];
    }

    const { originalWidth, originalHeight, x, y, width, height } = poiData;
    return [
      Math.round(x * originalWidth),
      Math.round(y * originalHeight),
      Math.round(width * originalWidth),
      Math.round(height * originalHeight)
    ];
  };

  const poiDimension = getPoiDimension();

  const getBaseUrl = (imageKey: string): string => {
    if (!aptomaHost) {
      return rtConfig.media.api.replace(':key', imageKey);
    }

    const aptomaConfig = rtConfig.aptoma?.image;
    if (!aptomaConfig) {
      console.warn('No aptoma config found');
      return '';
    }

    const apiUrl = aptomaDevHost ? aptomaConfig.apiDev : aptomaConfig.api;
    return apiUrl?.replace(':imageName', imageKey) || '';
  };

  const key = (): string => {
    if (!src) { return ''; }

    if (aptomaHost) {
      try {
        const url = new URL(src);
        const pathParts = url.pathname.split('/');
        return pathParts[pathParts.length - 1] || '';
      } catch {
        return '';
      }
    }

    const parts = src.split('/');
    for (let i = parts.length - 1; i >= 0; i--) {
      // eslint-disable-next-line no-useless-escape
      if (/^[a-zA-Z0-9!#\$%&'\(\)\*\+,\-\.\/:;\<=\>?@\[\]^_`{|}~\s\w]+$/.test(parts[i])) {
        return parts[i].includes('.') ? '' : parts[i];
      }
    }
    return '';
  };

  const getRatioPercentage = (width: number, height: number): RatioPercentage | null => {
    if (ratio === 'free') {
      return null;
    }
    const [ratioWidth, ratioHeight] = ratio.split('x').map(Number);
    const roundToTwoDecimals = (n: number) => +(Math.round(Number(n + 'e+2')) + 'e-2');
    return {
      original: roundToTwoDecimals(ratioWidth / ratioHeight),
      block: roundToTwoDecimals(Math.round(width) / Math.round(height))
    };
  };

  const getHeightByRatio = (width: number): number => {
    if (ratio === 'free' || !width) { return 0; }
    const [ratioWidth, ratioHeight] = ratio.split('x').map(Number);
    return Math.round((width * ratioHeight) / ratioWidth);
  };

  /**
   * Calculate crop coordinates based on focal point and desired aspect ratio.
   * This replicates the image API's focal point crop algorithm.
   * @param imageWidth - Original image width
   * @param imageHeight - Original image height
   * @param focalPoint - Focal point [x, y] normalized between 0 and 1
   * @param requestedRatio - Desired aspect ratio (width/height)
   * @returns Array [x, y, width, height] representing crop coordinates
   */
  const calculateCropFromFocalPoint = (
    imageWidth = 0,
    imageHeight = 0,
    focalPoint: number[] | undefined,
    requestedRatio: number | null | undefined
  ): number[] => {
    if (imageWidth === 0 || imageHeight === 0) {
      return [];
    }
    const fp = focalPoint?.length === 2 ? focalPoint : DEFAULT_FOCAL_POINT;
    const originalRatio = imageWidth / imageHeight;
    const targetRatio = requestedRatio || originalRatio;

    // Round to 1 decimal place to match dn-group-image precision
    const originalRatioRounded = Math.round(originalRatio * 10) / 10;
    const targetRatioRounded = Math.round(targetRatio * 10) / 10;

    const focalPointXPixel = imageWidth * fp[0];
    const focalPointYPixel = imageHeight * fp[1];

    let cropWidth: number, cropHeight: number, cropLeft: number, cropTop: number;

    if (originalRatioRounded === targetRatioRounded) {
      // No crop needed - same aspect ratio
      return [0, 0, imageWidth, imageHeight].map(Math.round);
    }

    if (originalRatioRounded > targetRatioRounded) {
      // Image is wider - crop horizontally
      cropWidth = imageHeight * targetRatioRounded;
      cropHeight = imageHeight;
      cropLeft = clamp(focalPointXPixel - cropWidth * CENTER_OFFSET, 0, imageWidth - cropWidth);
      cropTop = 0;
    } else {
      // Image is taller - crop vertically
      cropWidth = imageWidth;
      cropHeight = imageWidth / targetRatioRounded;
      cropLeft = 0;
      cropTop = clamp(focalPointYPixel - cropHeight * CENTER_OFFSET, 0, imageHeight - cropHeight);
    }

    return [Math.round(cropLeft), Math.round(cropTop), Math.round(cropWidth), Math.round(cropHeight)];
  };

  /**
   * Calculate crop coordinates based on AOI (Area of Interest) or focal point
   */
  const calculateCrop = (ratioPercentage: RatioPercentage | null): number[] | null => {
    // Match-AOI-based crop
    if (opt.isMatchAoi && poiDimension.length === 4) {
      const [aoiX, aoiY, aoiWidth, aoiHeight] = poiDimension;
      const innerCrop = calculateCropFromFocalPoint(aoiWidth, aoiHeight, MIDDLE_FOCAL_POINT, ratioPercentage?.block);
      return [
        aoiX + innerCrop[0],
        aoiY + innerCrop[1],
        innerCrop[2],
        innerCrop[3]
      ];
    }

    // Pre-defined crop instruction from delivery-api response
    const cropInstruction = opt.imageCrops?.[ratio];
    if (cropInstruction && ratioPercentage?.original === ratioPercentage?.block) {
      return cropInstruction;
    }

    // Focal point-based crop
    const crop = calculateCropFromFocalPoint(
      opt.originalWidth,
      opt.originalHeight,
      opt.fp,
      ratioPercentage?.block ?? null
    );

    // When original dimensions are missing and ratio is free, skip crop entirely
    if (crop.length === 0 && ratio === 'free') {
      return null;
    }

    return crop;
  };

  /**
   * Build Fastly Image Optimizer options object
   */
  const buildImageOptions = (
    crop: number[] | null,
    width: number,
    height: number,
    format: string
  ): FastlyImageApiPayload => ({
    ...(crop !== null ? { crop: formatCropParam(crop, width, height) } : {}),
    width: String(roundToFactor(width, rtConfig.imageWidthRoundingFactor)),
    format,
    quality: String(opt.quality || rtConfig.imageQuality || 80)
  });

  /**
   * Build URL with search params from options
   */
  const buildUrlWithParams = (baseUrl: string, options: FastlyImageApiPayload): URL => {
    const url = new URL(baseUrl);
    Object.entries(options).forEach(([key, value]) => {
      if (value) { url.searchParams.set(key, value); }
    });
    return url;
  };

  /**
   * Build Aptoma-specific URL with base64 encoded params
   */
  const buildAptomaUrl = (baseUrl: string, url: URL): string => {
    try {
      const srcUrl = new URL(src);
      srcUrl.searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
    } catch { /* ignore invalid source URL */ }

    return `${baseUrl}?io=${btoa(url.searchParams.toString())}`;
  };

  /**
   * Generate optimized image source URL
   */
  const getImageSrc = (format: string, width: number, height: number | null = null): string | false => {
    if (externalHost && !aptomaHost) {
      return src;
    }
    const imageKey = key();
    if (!width || !imageKey) { return false; }

    try {
      const finalHeight = height || getHeightByRatio(width);
      const baseUrl = getBaseUrl(imageKey);
      if (!baseUrl) { return false; }

      const ratioPercentage = getRatioPercentage(width, finalHeight);
      const crop = calculateCrop(ratioPercentage);
      const options = buildImageOptions(crop, width, finalHeight, format);
      const url = buildUrlWithParams(baseUrl, options);

      return aptomaHost ? buildAptomaUrl(baseUrl, url) : url.toString();
    } catch (e) {
      console.error('Error generating image src:', e);
      return false;
    }
  };

  return {
    key,
    getImageSrc,
    getRatio: () => ratio,
    getHeightByRatio
  };
}
